import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  fetchFollowingThunk,
  fetchFollowersThunk,
  followUserThunk,
  unfollowUserThunk,
} from '../../redux/user/following/userFollowActionThunks';
import { Users, UserPlus, UserMinus, Loader2, Search, UserCheck, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import CredibilityChip from '../Credibility/CredibilityChip';
import { resolveMediaUrl } from '../../utils/resolveMediaUrl';

export default function ConnectionsTab({ userId, isOwner = false }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // follow state
  const {
    followingList = [],
    followersList = [],
    byTargetId = {},
    loading,
    error,
  } = useSelector((s) => s.userFollow || {});

  // current user (for self-follow guard)
  const currentUserId = useSelector((s) => s.auth?.user?.id);

  const [view, setView] = useState('following'); // 'following' | 'followers'
  const [q, setQ] = useState('');
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    if (!userId) return;
    if (view === 'following') {
      dispatch(fetchFollowingThunk({ userId }));
    } else {
      dispatch(fetchFollowersThunk({ userId }));
    }
  }, [dispatch, userId, view]);

  const data = view === 'following' ? followingList : followersList;

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return data || [];
    return (data || []).filter((u) =>
      (u?.username || u?.name || '').toLowerCase().includes(needle)
    );
  }, [data, q]);

  const toggleFollow = async (u) => {
    const id = u?.id ?? u?.user_id;
    if (!id) return;

    // 🚫 do nothing if it's me
    if (currentUserId && id === currentUserId) return;

    const isFollowing = !!byTargetId[id];
    
    // Set loading state for this specific user
    setActionLoading(prev => ({ ...prev, [id]: true }));

    try {
      if (isFollowing) {
        await dispatch(unfollowUserThunk({ userId: id })).unwrap();
      } else {
        await dispatch(followUserThunk({ userId: id })).unwrap();
      }
    } catch (e) {
      console.warn('Follow toggle failed', e);
    } finally {
      setActionLoading(prev => ({ ...prev, [id]: false }));
    }
  };

  const ConnectionCard = ({ u }) => {
    const id = u?.id ?? u?.user_id;
    const username = u?.username || u?.name || `user-${id}`;
    const avatar = u?.profile_picture;
    const bio = u?.bio;
    const isFollowing = !!byTargetId[id];
    const isSelf = currentUserId && id === currentUserId;
    const isActionLoading = actionLoading[id];
    const profilePath = `/public-profiles/${encodeURIComponent(username)}`;

    return (
      <div className="bg-white dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-gray-700/50 p-4 hover:border-gray-300 dark:hover:border-gray-600/70 hover:bg-gray-50 dark:hover:bg-gray-800/70 transition-all duration-300 group">
        <div className="flex items-start gap-3 sm:gap-4">
          {/* Avatar */}
          <Link to={profilePath} className="shrink-0">
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 overflow-hidden shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-105">
              {avatar ? (
                <img
                  src={resolveMediaUrl(avatar)}
                  alt={username}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="text-white font-bold text-base sm:text-lg">
                    {(username || 'U').slice(0, 2).toUpperCase()}
                  </span>
                </div>
              )}
            </div>
          </Link>

          {/* User info */}
          <div className="flex-1 min-w-0">
            <Link 
              to={profilePath} 
              className="block group/link"
            >
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h3 className="text-gray-900 dark:text-white font-semibold text-sm sm:text-base truncate group-hover/link:text-blue-500 dark:group-hover/link:text-blue-400 transition-colors">
                  {username}
                </h3>
                <CredibilityChip cred={u?.credibility} size="xs" showAccuracy={false} />
                {isSelf && (
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-600/10 dark:bg-blue-600/20 text-blue-700 dark:text-blue-300 border border-blue-500/30 font-semibold shrink-0">
                    {t('connections.you')}
                  </span>
                )}
              </div>
            </Link>
            
            {bio && (
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 line-clamp-2 leading-relaxed">
                {bio}
              </p>
            )}
          </div>

          {/* Action button */}
          <div className="shrink-0 self-start">
            {!isSelf && id && (
              <button
                onClick={() => toggleFollow(u)}
                disabled={isActionLoading}
                className={`
                  px-3 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-medium
                  inline-flex items-center gap-1.5 sm:gap-2 border transition-all duration-300 whitespace-nowrap
                  disabled:opacity-50 disabled:cursor-not-allowed
                  ${isFollowing
                    ? 'border-green-500/40 bg-green-100 dark:bg-green-500/10 text-green-700 dark:text-green-300 hover:bg-green-500/20 hover:border-green-400/60'
                    : 'border-blue-500/40 bg-blue-100 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 hover:bg-blue-500/20 hover:border-blue-400/60'
                  }
                `}
              >
                {isActionLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : isFollowing ? (
                  <UserCheck className="w-4 h-4" />
                ) : (
                  <UserPlus className="w-4 h-4" />
                )}
                <span className="hidden sm:inline">
                  {isFollowing ? t('connections.following') : t('connections.follow')}
                </span>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        {/* Title and stats */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-600/10 rounded-xl border border-blue-500/20">
              <Users className="w-5 h-5 sm:w-6 sm:h-6 text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{t('connections.title')}</h2>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                {t('connections.statsLine', { following: followingList.length, followers: followersList.length })}
              </p>
            </div>
          </div>
        </div>

        {/* Tabs and Search */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          {/* View tabs */}
          <div className="flex gap-2 bg-gray-100 dark:bg-gray-800/50 border border-gray-300 dark:border-gray-700/60 rounded-xl p-1.5 backdrop-blur-sm">
            <button
              className={`
                flex-1 sm:flex-none px-4 sm:px-6 py-2.5 rounded-lg text-sm font-medium transition-all duration-300
                ${view === 'following'
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/25'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700/60 hover:text-gray-900 dark:hover:text-white'
                }
              `}
              onClick={() => setView('following')}
            >
              {t('connections.tabFollowing')}
            </button>
            <button
              className={`
                flex-1 sm:flex-none px-4 sm:px-6 py-2.5 rounded-lg text-sm font-medium transition-all duration-300
                ${view === 'followers'
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/25'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700/60 hover:text-gray-900 dark:hover:text-white'
                }
              `}
              onClick={() => setView('followers')}
            >
              {t('connections.tabFollowers')}
            </button>
          </div>

          {/* Search */}
          <div className="flex-1 relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
              <Search className="w-4 h-4 text-gray-400" />
            </div>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('connections.searchPlaceholder', { view: t(`connections.viewNames.${view}`) })}
              className="w-full bg-gray-100 dark:bg-gray-800/50 border border-gray-300 dark:border-gray-700/60 rounded-xl pl-10 pr-4 py-2.5 text-sm text-gray-800 dark:text-gray-200 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all backdrop-blur-sm"
            />
          </div>
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center gap-3 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>{t('connections.loadingView', { view: t(`connections.viewNames.${view}`) })}</span>
          </div>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="bg-red-900/20 border border-red-600/50 rounded-2xl p-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
            <div>
              <h3 className="text-red-200 font-medium mb-1">{t('connections.errorLoadingView', { view: t(`connections.viewNames.${view}`) })}</h3>
              <p className="text-red-300 text-sm">{String(error)}</p>
            </div>
          </div>
        </div>
      )}

      {/* List */}
      {!loading && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
          {filtered.map((u) => (
            <ConnectionCard key={u?.id ?? u?.user_id ?? u?.username} u={u} />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && (!filtered || filtered.length === 0) && (
        <div className="text-center py-16">
          <div className="bg-gray-100 dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-300 dark:border-gray-700/50 p-12 max-w-md mx-auto">
            <div className="w-16 h-16 mx-auto mb-6 bg-gray-200 dark:bg-gray-700/50 rounded-2xl flex items-center justify-center">
              <Users className="w-8 h-8 text-gray-500" />
            </div>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
              {q ? t('connections.noMatchesFound') : view === 'following' ? t('connections.noFollowingYet') : t('connections.noFollowersYet')}
            </h3>

            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
              {q
                ? t('connections.noViewMatch', { view: t(`connections.viewNames.${view}`), query: q })
                : view === 'following'
                ? isOwner
                  ? t('connections.startFollowingHint')
                  : t('connections.noFollowingToShow')
                : isOwner
                ? t('connections.shareProfileHint')
                : t('connections.noFollowersToShow')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}